import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { createApp as createVueApp } from 'vue';

const defaultDelimiters = ['{{', '}}'];

const describeMountTarget = target => {
    if (!target) {
        return null;
    }

    if (typeof target === 'string') {
        return target;
    }

    if (typeof Element !== 'undefined' && target instanceof Element) {
        const id = target.id ? `#${target.id}` : '';
        const className =
            typeof target.className === 'string' && target.className.trim()
                ? `.${target.className.trim().split(/\s+/).join('.')}`
                : '';
        return `<${target.tagName.toLowerCase()}${id}${className}>`;
    }

    return Object.prototype.toString.call(target);
};

const resolveMountTarget = target => {
    if (typeof target !== 'string') {
        return target;
    }

    if (typeof document === 'undefined') {
        return null;
    }

    try {
        return document.querySelector(target);
    } catch {
        return null;
    }
};

const installUseEntry = (app, entry) => {
    if (!entry) {
        return;
    }

    if (Array.isArray(entry)) {
        const [plugin, options] = entry;

        if (typeof options === 'undefined') {
            app.use(plugin);
            return;
        }

        app.use(plugin, options);
        return;
    }

    app.use(entry);
};

export const applyAppDefaults = (
    app,
    { delimiters = defaultDelimiters, globalComponents = {}, globalProperties = {} } = {}
) => {
    if (app.config?.compilerOptions && delimiters) {
        app.config.compilerOptions.delimiters = delimiters;
    }

    for (const [name, component] of Object.entries(globalComponents)) {
        app.component(name, component);
    }

    Object.assign(app.config.globalProperties, globalProperties);
};

export const mountApp = (app, target, label = 'Anonymous') => {
    const resolvedTarget = resolveMountTarget(target);

    if (!resolvedTarget) {
        console.error(
            `[VUE_MOUNT] ${label} target=${describeMountTarget(target) || 'undefined'} not found; mount skipped`
        );
        return null;
    }

    app.mount(resolvedTarget);
    return resolvedTarget;
};

export const createRootComponentApp = (
    rootComponent,
    {
        props = {},
        uses = [],
        globalComponents = {},
        globalProperties = {},
        delimiters = defaultDelimiters,
        configureApp,
    } = {}
) => {
    const app = createVueApp(rootComponent, props);

    uses.forEach(entry => installUseEntry(app, entry));

    applyAppDefaults(app, {
        delimiters,
        globalComponents,
        globalProperties,
    });

    if (configureApp) {
        configureApp(app);
    }

    return app;
};

export const mountRootOptions = (
    rootOptions,
    {
        selector = rootOptions.el,
        uses = [],
        globalComponents = {},
        globalProperties = {},
        delimiters = rootOptions.delimiters || defaultDelimiters,
        configureApp,
    } = {}
) => {
    const { el, router, store, ...componentOptions } = rootOptions;
    const app = createVueApp(componentOptions);
    const resolvedUses = [];

    if (store) {
        resolvedUses.push(store);
    }

    if (router) {
        resolvedUses.push(router);
    }

    resolvedUses.push(...uses);
    resolvedUses.forEach(entry => installUseEntry(app, entry));

    applyAppDefaults(app, {
        delimiters,
        globalComponents,
        globalProperties,
    });

    if (configureApp) {
        configureApp(app);
    }

    mountApp(app, selector, componentOptions.name || componentOptions.__file || selector || 'Anonymous');

    return app;
};

export const mountRootComponent = (
    rootComponent,
    {
        selector,
        props = {},
        uses = [],
        globalComponents = {},
        globalProperties = {},
        delimiters = defaultDelimiters,
        configureApp,
    } = {}
) => {
    const app = createRootComponentApp(rootComponent, {
        props,
        uses,
        globalComponents,
        globalProperties,
        delimiters,
        configureApp,
    });

    mountApp(app, selector, rootComponent?.name || rootComponent?.__name || selector || 'Anonymous');

    return app;
};

export const createApp = createVueApp;
